import { useEffect, useState } from "react";
import { fetchHealthScore, fetchVersion } from "../lib/api";
import type { HealthScore, VersionInfo } from "../lib/types";
import StatCard from "../components/StatCard";
import HealthIndicatorRow from "../components/HealthIndicator";
import WriteOpsHint from "../components/WriteOpsHint";
import { SYSTEM_WRITE_OPS } from "../writeops/system";

export default function SystemRoute() {
  const [version, setVersion] = useState<VersionInfo | null>(null);
  const [health, setHealth] = useState<HealthScore | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [v, h] = await Promise.all([fetchVersion(), fetchHealthScore()]);
        setVersion(v);
        setHealth(h);
      } catch (e: any) {
        setError(e.message ?? String(e));
      }
    })();
  }, []);

  // Only schema drift is actionable (replay/migration); version-only drift is
  // cosmetic (same schema_version, read-side bump — self-heals on next boot).
  const drift = version?.pack_drift ?? [];
  const schemaDrift = drift.filter((d) => d.kind === "schema");
  const versionDrift = drift.filter((d) => d.kind !== "schema");

  return (
    <div className="px-8 py-6 space-y-6">
      <h2 className="text-lg font-medium">System</h2>
      {error && <div className="text-red-400 text-sm">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="KW Version"
          value={version?.version ?? "—"}
          hint={version ? `released ${version.released_at}` : undefined}
        />
        <StatCard
          label="Alembic Head"
          value={version?.alembic_head ?? "—"}
        />
        <StatCard
          label="Bundled Packs"
          value={
            version ? Object.keys(version.bundled_packs).length : "—"
          }
          hint={
            version
              ? `${version.installed_packs.length} installed`
              : undefined
          }
        />
        <StatCard
          label="Pack-Drift"
          value={version ? schemaDrift.length : "—"}
          tone={schemaDrift.length > 0 ? "warn" : "ok"}
          hint={
            schemaDrift.length > 0
              ? "Schema-Drift — kontextwerk-pack replay nötig"
              : versionDrift.length > 0
                ? `${versionDrift.length} kosmetisch (kein Replay)`
                : undefined
          }
        />
      </div>

      {health && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm uppercase tracking-wide text-kw-text-muted">
              Health Score
            </h3>
            <div className="text-xs text-kw-text-muted">
              total{" "}
              <span className="text-kw-text-primary font-mono">
                {health.total_score.toFixed(2)}
              </span>{" "}
              · tier{" "}
              <span className="text-kw-l2">{health.tier}</span>
            </div>
          </div>
          <div className="bg-kw-bg-elevated rounded p-3">
            {Object.entries(health.indicators).map(([id, ind]) => (
              <HealthIndicatorRow key={id} id={id} indicator={ind} />
            ))}
          </div>
        </div>
      )}

      {schemaDrift.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm uppercase tracking-wide text-kw-text-muted">
            Pack Drift — Schema (Replay/Migration nötig)
          </h3>
          <ul className="text-xs space-y-1 bg-kw-bg-elevated rounded p-3">
            {schemaDrift.map((d) => (
              <li key={d.pack_id} className="flex justify-between font-mono">
                <span>{d.pack_id}</span>
                <span className="text-kw-l4">
                  s{d.installed_schema ?? "?"} → s{d.bundled_schema ?? "?"}{" "}
                  (bundled {d.bundled} → installed {d.installed})
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {versionDrift.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm uppercase tracking-wide text-kw-text-muted">
            Pack Drift — Version (kosmetisch, kein Replay)
          </h3>
          <ul className="text-xs space-y-1 bg-kw-bg-elevated rounded p-3 opacity-70">
            {versionDrift.map((d) => (
              <li key={d.pack_id} className="flex justify-between font-mono">
                <span>{d.pack_id}</span>
                <span className="text-kw-text-muted">
                  bundled {d.bundled} → installed {d.installed} · schema s
                  {d.installed_schema ?? "?"} unverändert
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <WriteOpsHint title="System-Write-Ops" ops={SYSTEM_WRITE_OPS} />
    </div>
  );
}
